import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Spin,
  Typography,
  Row,
  Col,
  Divider,
  Switch,
} from 'antd';
import {
  BankOutlined,
  UserOutlined,
  NumberOutlined,
  SaveOutlined,
  ReloadOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';

const { Title, Text } = Typography;

const ConfiguracionBot = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadConfiguracion();
  }, []);

  const loadConfiguracion = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/configuracion-bot');
      if (response.ok) {
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          form.setFieldsValue({
            titularCuenta: data.cfg_titular_cuenta || '',
            nombreBanco: data.cfg_nombre_banco || '',
            cbu: data.cfg_cbu || '',
            alias: data.cfg_alias || '',
            numeroCuenta: data.cfg_numero_cuenta || '',
            descuentoAutomatico: data.cfg_descuento_automatico !== false, // Por defecto true si no existe
          });
        }
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al cargar la configuración');
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error);
      message.error('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    try {
      setSaving(true);
      // Asegurar que descuentoAutomatico sea un booleano
      const payload = {
        ...values,
        descuentoAutomatico: values.descuentoAutomatico !== false,
      };
      const response = await apiClient.post('/configuracion-bot', payload);
      if (response.ok) {
        message.success('Configuración guardada correctamente');
        loadConfiguracion();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al guardar la configuración');
      }
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      message.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div style={{ 
          padding: '50px', 
          textAlign: 'center',
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? '16px' : '24px' }}>
        <Title level={isMobile ? 4 : 2} style={{ marginBottom: '24px', color: '#e0e0e0' }}>
          Configuración del Bot
        </Title>

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={16} xl={12}>
            <Card
              title={
                <span>
                  <BankOutlined style={{ marginRight: 8 }} />
                  Datos de Cuenta Bancaria para Transferencias
                </span>
              }
              extra={
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadConfiguracion}
                  size="small"
                >
                  Recargar
                </Button>
              }
            >
              <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
                Estos datos se mostrarán a los alumnos cuando elijan pagar por transferencia 
                a través del bot de WhatsApp.
              </Text>

              <Divider />

              <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
                autoComplete="off"
              >
                <Form.Item
                  name="descuentoAutomatico"
                  label={
                    <span>
                      <CreditCardOutlined style={{ marginRight: 8 }} />
                      Descuento Automático de Créditos
                    </span>
                  }
                  valuePropName="checked"
                  style={{ marginBottom: '32px' }}
                >
                  <Switch 
                    checkedChildren="Activado" 
                    unCheckedChildren="Desactivado"
                    size="default"
                  />
                </Form.Item>
                <Text type="secondary" style={{ display: 'block', marginBottom: '24px', marginTop: '-16px' }}>
                  Cuando está activado, el sistema descuenta automáticamente los créditos de los alumnos 
                  que asistieron a las clases cada hora. Si está desactivado, los créditos no se descontarán automáticamente.
                </Text>

                <Divider />

                <Form.Item
                  name="titularCuenta"
                  label="Titular de la Cuenta"
                  rules={[
                    { required: true, message: 'Ingrese el titular de la cuenta' },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="Nombre completo del titular"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="nombreBanco"
                  label="Nombre del Banco"
                  rules={[
                    { required: true, message: 'Ingrese el nombre del banco' },
                  ]}
                >
                  <Input
                    prefix={<BankOutlined />}
                    placeholder="Ej: Banco Santander, Banco Galicia, etc."
                    size="large"
                  />
                </Form.Item>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="cbu"
                      label="CBU"
                      rules={[
                        { 
                          pattern: /^[0-9]{22}$/, 
                          message: 'El CBU debe tener 22 dígitos numéricos' 
                        },
                      ]}
                    >
                      <Input
                        prefix={<NumberOutlined />}
                        placeholder="22 dígitos"
                        size="large"
                        maxLength={22}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="alias"
                      label="Alias"
                    >
                      <Input
                        placeholder="Ej: mi.alias.banco"
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="numeroCuenta"
                  label="Número de Cuenta"
                >
                  <Input
                    prefix={<NumberOutlined />}
                    placeholder="Número de cuenta bancaria"
                    size="large"
                  />
                </Form.Item>

                <Divider />

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={saving}
                    size="large"
                    block={isMobile}
                  >
                    Guardar Configuración
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>

          <Col xs={24} lg={8} xl={12}>
            <Card title="Vista previa del mensaje">
              <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                Así se verá el mensaje en WhatsApp cuando el alumno elija pagar por transferencia:
              </Text>
              
              <div style={{
                background: '#075e54',
                padding: '16px',
                borderRadius: '8px',
                color: '#fff',
                fontFamily: 'system-ui, -apple-system, sans-serif',
              }}>
                <p style={{ margin: '0 0 12px 0', fontWeight: 'bold' }}>
                  💳 Datos para Transferencia:
                </p>
                <p style={{ margin: '4px 0' }}>
                  <strong>Titular:</strong> {form.getFieldValue('titularCuenta') || '---'}
                </p>
                <p style={{ margin: '4px 0' }}>
                  <strong>Banco:</strong> {form.getFieldValue('nombreBanco') || '---'}
                </p>
                {form.getFieldValue('cbu') && (
                  <p style={{ margin: '4px 0' }}>
                    <strong>CBU:</strong> {form.getFieldValue('cbu')}
                  </p>
                )}
                {form.getFieldValue('alias') && (
                  <p style={{ margin: '4px 0' }}>
                    <strong>Alias:</strong> {form.getFieldValue('alias')}
                  </p>
                )}
                {form.getFieldValue('numeroCuenta') && (
                  <p style={{ margin: '4px 0' }}>
                    <strong>Nro. Cuenta:</strong> {form.getFieldValue('numeroCuenta')}
                  </p>
                )}
                <p style={{ margin: '12px 0 0 0', fontSize: '12px', opacity: 0.8 }}>
                  📎 Envía el comprobante de transferencia para confirmar tu pago.
                </p>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </MainLayout>
  );
};

export default ConfiguracionBot;
